'use client';

import { useId } from 'react';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { useProductPriceDisplay } from '@eliadmin/core/src/components/nt/product/product-price';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { SelectedValue } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { stockState } from './use-active-variant';

/** Values of the variant in the attribute order („Čierna · 1600“). */
function valueLabel(variant: VariantsEntity) {
  return [...(variant.values ?? [])]
    .sort((a, b) => (a.attribute_position ?? 0) - (b.attribute_position ?? 0))
    .map(value => value.name)
    .filter(Boolean)
    .join(' · ');
}

function VariantChip({
  variant,
  selected,
  onSelect,
}: {
  variant: VariantsEntity;
  selected: boolean;
  onSelect: () => void;
}) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const price = useProductPriceDisplay(variant);
  // The live API hides the variant values (attributes not shown in the detail) – the chip falls back
  // to the variant name, then to its code.
  const label = valueLabel(variant) || variant.name?.trim() || variant.code;
  const { sellable } = stockState(variant);

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'nt-body-m-semibold flex min-h-10 max-w-full flex-wrap items-center gap-x-2 rounded-xl border px-4 py-2 text-left transition-colors',
        'focus-visible:shadow-focus focus-visible:outline-none',
        selected
          ? 'border-[#0D110D] bg-[#0D110D] text-white'
          : 'border-[#E2E5DF] bg-white hover:border-[#CDD2CA] hover:bg-[#F5F6F3]',
        !selected && (sellable ? 'text-[#0D110D]' : 'text-[#5D655D] line-through'),
      )}
    >
      <span>{label}</span>
      {!price.priceOnRequest ? (
        <span className="flex flex-wrap gap-x-2 text-sm">
          {price.showMocVocPrices ? (
            <span className="font-normal">
              MOC: {formatPrice(price.catalogRetailWithVat, currency, currencySettings)}
            </span>
          ) : null}
          <span>
            {price.showMocVocPrices ? 'VOC: ' : ''}
            {formatPrice(price.currentWithVat, currency, currencySettings)}
          </span>
        </span>
      ) : null}
    </button>
  );
}

/**
 * Figma „Variant“ (112:1289): „Varianta:“ and one chip per variant – „values · price“, the selected
 * one dark. Selection goes through the core variant store (price, stock, cart, `?vid=`). A sold-out
 * variant stays selectable (its state shows in the stock row) with a struck-through secondary label
 * (#5D655D keeps AA contrast).
 */
export function VariantChips({ active }: { active?: VariantsEntity }) {
  const { t } = useTranslations();
  const labelId = useId();
  const variants = useVariantsStore(state => state.product.variants);
  const attributes = useVariantsStore(state => state.product.attributes);
  const setSelectedValues = useVariantsStore(state => state.setSelectedValues);
  const setSelectedVariant = useVariantsStore(state => state.setSelectedVariant);
  if (!variants?.length) return null;

  // The whole value set of the variant replaces the selection: `setSelectedVariant` alone keeps the
  // values already selected per attribute, so `?vid=` would stay on the previous variant. Without
  // values (live API) the variant is selected directly – the core then has no `vid` to write.
  const select = (variant: VariantsEntity) => {
    const values: SelectedValue[] = [];
    for (const value of variant.values ?? []) {
      const attribute = attributes?.find(item => item.id === value.attribute_id);
      const attributeValue = attribute?.values?.find(item => item.id === value.id);
      if (attribute && attributeValue) values.push({ attribute, value: attributeValue });
    }
    if (values.length && values.length === variant.values?.length) setSelectedValues(values);
    else setSelectedVariant(variant);
  };

  return (
    <div className="flex flex-col gap-3">
      <p id={labelId} className="nt-body-l-bold text-[#0D110D]">
        {`${t('text3731')}:`}
      </p>
      <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-2">
        {variants.map(variant => (
          <VariantChip
            key={variant.id}
            variant={variant}
            selected={variant.id === active?.id}
            onSelect={() => select(variant)}
          />
        ))}
      </div>
    </div>
  );
}
